/**
 * Site-wide constants.
 *
 * `SITE_ORIGIN` drives canonicals, OG URLs and the sitemap. It must match the
 * production hostname exactly — a mismatch makes every canonical point at the
 * wrong site, which is worse than having none.
 *
 * `VITE_SITE_ORIGIN` is set at build time (Railway sets it per environment:
 * production → `https://www.singulate.com`, preview →
 * `https://testing-marketing.singulate.com`). The fallback matches the current
 * production hostname so local builds and unconfigured environments keep
 * working — never leave it unset for a deploy to a non-production hostname.
 */
export const SITE_ORIGIN =
  (import.meta.env.VITE_SITE_ORIGIN as string | undefined) ?? 'https://www.singulate.com'
export const SITE_NAME = 'Singulate'
export const DEFAULT_OG_IMAGE = '/media/og-singulate.png'

/** Titles in the capture are `<page> - Singulate`, with the homepage as-is. */
export function pageTitle(title: string): string {
  if (!title) return SITE_NAME
  return title.includes(SITE_NAME) ? title : `${title} - ${SITE_NAME}`
}

/**
 * Absolute URL for a site path.
 *
 * Media filenames carry spaces and parentheses straight from Webflow —
 * `Frame 7 (3).png` — and an unencoded space in an og:image breaks every
 * scraper that reads it. Each segment is encoded, the slashes are not.
 */
export function absolute(pathOrUrl: string | null | undefined): string | undefined {
  if (!pathOrUrl) return undefined
  if (/^https?:/.test(pathOrUrl)) return pathOrUrl
  const p = pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`
  const encoded = p
    .split('/')
    .map((seg) => encodeURIComponent(decodeURIComponent(seg)))
    .join('/')
  return `${SITE_ORIGIN}${encoded}`
}
